import { Entity, EntityTab } from '../../types/builder';
import PropertiesTab from './PropertiesTab';
import CrudTab from './CrudTab';
import CustomEndpointsTab from './CustomEndpointsTab';

interface Props {
  entity: Entity;
  onUpdate: (entity: Entity) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  maxProperties: number;
  maxCustomEndpoints: number;
  target: 'dotnet' | 'java';  // ← ADD
}

const EntityCard = ({
  entity, onUpdate, onDelete, onDuplicate, maxCustomEndpoints, target
}: Props) => {

  const setTab = (tab: EntityTab) => {
    onUpdate({ ...entity, activeTab: tab });
  };

  const toggleExpand = () => {
    onUpdate({ ...entity, expanded: !entity.expanded });
  };

  const customCount = entity.customEndpoints.length;

  return (
    <div className={`bg-gray-900 border
                     rounded-2xl mb-3
                     overflow-hidden
                     transition-colors
                     ${entity.expanded
                       ? 'border-blue-800/50'
                       : 'border-gray-800'
                     }`}>

      {/* Header */}
      <div
        className="flex items-center gap-3
                   px-4 py-3 cursor-pointer
                   hover:bg-gray-800/50
                   transition-colors"
        onClick={toggleExpand}>

        {/* Icon */}
        <div className="w-7 h-7
                        bg-blue-500/10
                        border border-blue-500/20
                        rounded-lg flex items-center
                        justify-center text-xs
                        flex-shrink-0">
          📦
        </div>

        {/* Name Input */}
        <input
          value={entity.name}
          onChange={e => onUpdate({
            ...entity, name: e.target.value
          })}
          onClick={e => e.stopPropagation()}
          className="bg-transparent border-none
                     text-white font-bold
                     text-base outline-none
                     flex-1 font-sans
                     focus:text-blue-300
                     transition-colors"
          placeholder="EntityName"
        />

        {/* Meta */}
        <span className="text-gray-600
                         font-mono text-xs
                         whitespace-nowrap">
          {entity.properties.length}p
          {customCount > 0
            ? ` · ${customCount} custom`
            : ''
          }
        </span>

        {/* Actions */}
        <div className="flex gap-1"
             onClick={e => e.stopPropagation()}>
          <button
            onClick={onDuplicate}
            className="text-gray-600
                       hover:text-gray-300
                       hover:bg-gray-700
                       rounded-md p-1.5
                       text-xs transition-colors"
            title="Duplicate">
            ⧉
          </button>
          <button
            onClick={onDelete}
            className="text-gray-600
                       hover:text-red-400
                       hover:bg-red-500/10
                       rounded-md p-1.5
                       text-xs transition-colors"
            title="Delete">
            ✕
          </button>
        </div>

        {/* Expand indicator */}
        <span className="text-gray-600 text-xs
                         transition-transform
                         duration-200"
              style={{
                transform: entity.expanded
                  ? 'rotate(180deg)'
                  : 'rotate(0deg)'
              }}>
          ▾
        </span>
      </div>

      {/* Expanded Content */}
      {entity.expanded && (
        <>
          {/* Tabs */}
          <div className="flex border-t border-b
                          border-gray-800
                          bg-gray-900/80">
            {([
              { key: 'properties', label: 'Properties' },
              { key: 'crud',       label: 'CRUD' },
              { key: 'custom',     label: `Custom${customCount > 0 ? ` (${customCount})` : ''}` },
            ] as { key: EntityTab; label: string }[])
              .map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setTab(tab.key)}
                  className={`px-4 py-2.5
                              text-xs font-medium
                              font-mono border-b-2
                              transition-colors
                              ${entity.activeTab === tab.key
                                ? 'text-blue-400 border-blue-500'
                                : 'text-gray-500 border-transparent hover:text-gray-300'
                              }`}>
                  {tab.label}
                </button>
              ))}
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {entity.activeTab === 'properties' && (
              <PropertiesTab
                entity={entity}
                onUpdate={onUpdate}
              />
            )}
            {entity.activeTab === 'crud' && (
              <CrudTab
                entity={entity}
                onUpdate={onUpdate}
              />
            )}
            {entity.activeTab === 'custom' && (
            <CustomEndpointsTab
                entity={entity}
                onUpdate={onUpdate}
                maxCustomEndpoints={maxCustomEndpoints}
                target={target}  // ← ADD
            />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EntityCard;